import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const inputCls =
  "w-full bg-[#060b14] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2a5fdb] transition-colors";

const rise = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-15%" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const Countdown = ({ startIso, tid }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(startIso).getTime() - now;
  if (diff <= 0) {
    return (
      <span data-testid={tid} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#2a5fdb]">
        Program underway
      </span>
    );
  }
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const Unit = ({ v, l }) => (
    <span className="flex flex-col items-center border border-white/15 bg-[#0a1428] px-2.5 py-1.5 min-w-[52px]">
      <span className="font-display text-xl leading-none text-white">{v}</span>
      <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/40 mt-1">{l}</span>
    </span>
  );
  return (
    <div data-testid={tid} className="flex items-center gap-2">
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#2a5fdb]">Starts in</span>
      <Unit v={days} l="days" />
      <Unit v={hours} l="hrs" />
      <Unit v={mins} l="min" />
    </div>
  );
};

export const RepProgram = () => {
  const [data, setData] = useState(null);
  const [signupFor, setSignupFor] = useState(null); // "aspiring" | "current" | null

  const load = () => api.get("/rep-program").then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;

  return (
    <section
      id="whats-new"
      data-testid="rep-program-section"
      className="relative py-24 md:py-36 px-6 md:px-12 bg-[#0a1428] border-y border-white/10"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-4">
            <div className="chapter-num mb-4">What's new</div>
            <span className="inline-block font-mono text-[10px] uppercase tracking-[0.25em] bg-[#2a5fdb] text-white px-3 py-1.5">
              $150 upfront · 6 weeks
            </span>
            <span className="mt-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
              Starts 30 Sep · works out to $25 a session
            </span>
          </div>
          <div className="col-span-12 md:col-span-8">
            <motion.h2
              {...rise}
              className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight"
            >
              Next Step
              <br />
              <span className="text-[#2a5fdb]">Rep Program.</span>
            </motion.h2>
            <motion.p
              {...rise}
              transition={{ ...rise.transition, delay: 0.1 }}
              className="mt-6 text-white/70 max-w-xl text-base md:text-lg leading-relaxed"
            >
              A 6-week early-morning block built for U12–U18 rep trials — for
              players born 2008–2018. One small Wednesday-morning group capped
              at 6, and at{" "}
              <span className="text-white font-semibold">$150 upfront</span> for
              all six weeks (works out to just{" "}
              <span className="text-white font-semibold">$25 a session</span>),
              it's serious rep prep without the serious price tag.
            </motion.p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 max-w-3xl">
          {Object.entries(data.programs).map(([key, p], i) => {
            const full = p.spots_left === 0;
            return (
              <motion.div
                key={key}
                {...rise}
                transition={{ ...rise.transition, delay: 0.1 * i }}
                data-testid={`rep-card-${key}`}
                className="border border-white/10 bg-[#060b14] p-6 md:p-8 flex flex-col gap-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-none">
                      {p.label}
                    </h3>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                      {p.sub}
                    </p>
                  </div>
                  {full && (
                    <span
                      data-testid={`rep-spots-${key}`}
                      className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-red-400/50 text-red-400"
                    >
                      FULL
                    </span>
                  )}
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.15em]">
                  <div>
                    <dt className="text-white/40">When</dt>
                    <dd className="text-white mt-1">{p.day} · {p.time}</dd>
                  </div>
                  <div>
                    <dt className="text-white/40">Dates</dt>
                    <dd className="text-white mt-1">{p.dates}</dd>
                  </div>
                  <div>
                    <dt className="text-white/40">Ages</dt>
                    <dd className="text-white mt-1">Born 2008–2018 · U12–U18</dd>
                  </div>
                  <div>
                    <dt className="text-white/40">Price</dt>
                    <dd className="text-white mt-1">{data.price} upfront · all 6 weeks</dd>
                  </div>
                </dl>
                <Countdown startIso={p.start_iso} tid={`rep-countdown-${key}`} />
                <button
                  data-testid={`rep-signup-btn-${key}`}
                  disabled={full}
                  onClick={() => setSignupFor(key)}
                  className="btn-sharp self-start disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {full ? "Group full" : "Claim a spot"}
                  <span aria-hidden>→</span>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
      <RepSignupDialog
        program={signupFor}
        programs={data.programs}
        onClose={() => { setSignupFor(null); load(); }}
      />
    </section>
  );
};

const RepSignupDialog = ({ program, programs, onClose }) => {
  const [form, setForm] = useState({
    parent_name: "", player_name: "", birth_year: "2015", phone: "", email: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // signup object
  const [paying, setPaying] = useState(false);

  const p = program ? programs[program] : null;
  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const canSubmit =
    form.parent_name.trim() && form.player_name.trim() && form.phone.trim() && form.email.trim() && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/rep-program/signup", {
        program,
        ...form,
        birth_year: Number(form.birth_year),
        notes: form.notes || null,
      });
      setDone(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const payNow = async () => {
    if (paying || !done) return;
    setPaying(true);
    try {
      const { data } = await api.post("/payments/rep-checkout", {
        signup_id: done.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      setPaying(false);
    }
  };

  return (
    <Dialog open={!!program} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="rep-signup-dialog"
        className="bg-[#0a1428] border-white/15 text-white sm:max-w-md max-h-[90vh] overflow-y-auto"
      >
        {done ? (
          <div data-testid="rep-signup-success" className="py-2">
            <div className="w-12 h-12 border border-[#2a5fdb] flex items-center justify-center mb-6">
              <Check className="text-[#2a5fdb]" />
            </div>
            <h3 className="font-display uppercase text-3xl tracking-tight leading-none">
              Spot <span className="text-[#2a5fdb]">claimed.</span>
            </h3>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">
              {done.player_name} is in — {p?.label}, {p?.day} {p?.time},{" "}
              {p?.dates}. A confirmation email is on its way.
            </p>
            <button
              data-testid="rep-pay-btn"
              onClick={payNow}
              disabled={paying}
              className="btn-sharp mt-6 disabled:opacity-40"
            >
              {paying ? "Redirecting…" : "Pay $150 now by card"}
              <span aria-hidden>→</span>
            </button>
            <p className="mt-3 text-white/40 text-xs">
              Or pay later — there's a card link in your email, and cash / bank
              transfer works too.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display uppercase text-3xl tracking-tight leading-none text-left">
                {p?.label}
              </DialogTitle>
              <DialogDescription className="text-white/60 text-sm text-left">
                {p?.sub} · {p?.day} {p?.time} · {p?.dates} · $150 upfront covers
                all 6 weeks (works out to $25 a session) · players born 2008–2018.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <input data-testid="rep-input-parent" className={inputCls} placeholder="Parent name *" value={form.parent_name} onChange={setField("parent_name")} />
              <input data-testid="rep-input-player" className={inputCls} placeholder="Player name *" value={form.player_name} onChange={setField("player_name")} />
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                Player's birth year
                <select
                  data-testid="rep-input-birth-year"
                  className={`${inputCls} mt-1`}
                  value={form.birth_year}
                  onChange={setField("birth_year")}
                >
                  {[2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </label>
              <input data-testid="rep-input-phone" className={inputCls} type="tel" placeholder="Phone *" value={form.phone} onChange={setField("phone")} />
              <input data-testid="rep-input-email" className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={setField("email")} />
              <textarea data-testid="rep-input-notes" className={`${inputCls} min-h-[70px] resize-y`} placeholder="Anything Owen should know? (optional)" value={form.notes} onChange={setField("notes")} />
              {error && <p data-testid="rep-signup-error" className="text-red-400 text-sm">{error}</p>}
              <button
                data-testid="rep-signup-submit"
                type="submit"
                disabled={!canSubmit}
                className="btn-sharp w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Claiming…" : "Claim the spot"}
                <span aria-hidden>→</span>
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
